import { useState, useEffect } from 'react';
import { X, Minus, Plus, Trash2, Loader2, ShoppingCart, Info, MapPin, Store, Truck, Gift, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCart, formatAUD } from '@/context/CartContext';
import { createCheckoutSession, formatApiErrorDetail, apiClient } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const MODE_META = {
  pickup:   { icon: Store, label: 'Pickup', hint: 'Malaga workshop' },
  metro:    { icon: Truck, label: 'Metro', hint: 'Perth · Wed & Fri' },
  regional: { icon: MapPin, label: 'Regional', hint: 'WA · Fri only' },
};

export default function CartDrawer() {
  const {
    items, drawerOpen, closeDrawer, config, slots, subtotal, shipping, total, meetsMin,
    fulfilment, setFulfilmentField, slotChosen, totalSavings,
    recurring, setRecurring,
    referralCode, setReferralCode,
    increment, decrement, setQty, remove, clear,
  } = useCart();

  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [refInput, setRefInput] = useState('');
  const [refCheck, setRefCheck] = useState({ status: 'idle', message: '', valid: false });

  useEffect(() => { if (drawerOpen) setError(null); }, [drawerOpen]);
  useEffect(() => {
    // Sync input from stored code (e.g., ?ref= URL)
    if (referralCode) setRefInput(referralCode);
  }, [referralCode]);
  // Auto-validate applied referral code whenever cart subtotal changes
  useEffect(() => {
    if (!referralCode || subtotal <= 0) {
      setRefCheck({ status: 'idle', message: '', valid: false });
      return undefined;
    }
    setRefCheck((prev) => ({ ...prev, status: 'checking' }));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.post('/customer/referral/validate', {
          code: referralCode,
          subtotal,
          email: form.email || undefined,
        });
        if (cancelled) return;
        setRefCheck({
          status: 'done',
          valid: !!data.valid,
          message: data.valid ? '$20 off applied at checkout' : (data.error || 'Invalid code'),
        });
      } catch {
        if (cancelled) return;
        setRefCheck({ status: 'done', valid: false, message: 'Could not verify code' });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [referralCode, subtotal, form.email]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const h = (e) => e.key === 'Escape' && closeDrawer();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [drawerOpen, closeDrawer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const modeCfg = slots?.modes?.[fulfilment.mode];
  const currentWindows = modeCfg?.windows || [];
  const currentDates = modeCfg?.dates || [];

  const onModeChange = (mode) => {
    setFulfilmentField({ mode, slot_date: '', slot_window: '' });
  };

  const onCheckout = async (e) => {
    e.preventDefault();
    setError(null);
    if (items.length === 0) { setError('Cart is empty'); return; }
    if (!meetsMin) { setError(`Minimum order is ${formatAUD(config?.min_order_total || 50)}`); return; }
    if (!slotChosen) { setError('Please choose a delivery or pickup slot'); return; }
    setSubmitting(true);
    try {
      trackEvent('Checkout Started', {
        items: items.length,
        recurring: recurring ? 'yes' : 'no',
        mode: fulfilment.mode || 'delivery',
      });
      const res = await createCheckoutSession({
        items: items.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity) })),
        customer_email: form.email,
        customer_name: form.name,
        customer_phone: form.phone,
        origin_url: window.location.origin,
        fulfilment: {
          mode: fulfilment.mode,
          slot_date: fulfilment.slot_date,
          slot_window: fulfilment.slot_window,
        },
        ...(recurring ? { recurring } : {}),
        ...(referralCode && refCheck.valid ? { referral_code: referralCode } : {}),
      });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const freeThreshold = modeCfg?.free_over ?? config?.shipping_rules?.[fulfilment.mode]?.free_over ?? 0;
  const freeRemaining = freeThreshold && subtotal < freeThreshold ? freeThreshold - subtotal : 0;
  const minOrderShort = Math.max(0, (config?.min_order_total || 50) - subtotal);
  const bulkKg = config?.bulk_threshold_kg ?? 10;
  const bulkPct = Math.round((config?.bulk_discount_pct ?? 0.1) * 100);

  return (
    <>
      <div
        aria-hidden={!drawerOpen}
        onClick={closeDrawer}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        data-testid="cart-drawer"
        aria-hidden={!drawerOpen}
        className={`fixed z-50 top-0 right-0 h-full w-full sm:w-[520px] bg-[var(--mw-bg)] border-l border-white/10 shadow-2xl overflow-y-auto transition-transform duration-500 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="sticky top-0 z-10 bg-[var(--mw-bg)]/95 backdrop-blur border-b border-white/10 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart size={18} className="text-[var(--mw-primary)]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Your Cart</div>
              <h2 className="font-display text-2xl mt-0.5 text-white">
                {items.length} item{items.length === 1 ? '' : 's'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            data-testid="cart-drawer-close"
            className="text-white/60 hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </header>

        {items.length === 0 ? (
          <div data-testid="cart-drawer-empty" className="px-6 py-16 text-center">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 mb-4">Empty</div>
            <h3 className="font-display text-2xl text-white">Your cart is empty</h3>
            <p className="mt-3 text-white/55 text-sm max-w-xs mx-auto leading-relaxed">
              Browse the shop and add cuts to check out with cold-chain delivery.
            </p>
            <button
              type="button"
              onClick={() => {
                closeDrawer();
                setTimeout(() => document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' }), 60);
              }}
              className="mt-6 text-[11px] uppercase tracking-[0.22em] bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white px-6 py-3 transition-colors"
            >
              Browse shop
            </button>
          </div>
        ) : (
          <div>
            <ul data-testid="cart-items-list" className="divide-y divide-white/10">
              {items.map((it) => (
                <li key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-white leading-tight">{it.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mt-1">
                        {it.category} · {formatAUD(it.effectiveUnit)}/{it.unit}
                        {it.isBulk && <span className="line-through opacity-50 ml-1.5">{formatAUD(it.price)}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(it.product_id)}
                      data-testid={`cart-item-remove-${it.product_id}`}
                      className="text-white/45 hover:text-[var(--mw-primary)] transition-colors"
                      aria-label={`Remove ${it.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {it.isBulk && (
                    <div
                      data-testid={`cart-item-bulk-${it.product_id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1"
                    >
                      Bulk {bulkPct}% off · Saving {formatAUD(it.savings)}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center border border-white/15">
                      <button
                        type="button"
                        onClick={() => decrement(it.product_id)}
                        data-testid={`cart-item-decrement-${it.product_id}`}
                        className="w-8 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        aria-label="Decrease"
                      >
                        <Minus size={12} />
                      </button>
                      <input
                        type="number"
                        min={it.min}
                        step={it.step}
                        value={it.quantity}
                        onChange={(e) => setQty(it.product_id, e.target.value)}
                        data-testid={`cart-item-qty-${it.product_id}`}
                        className="w-16 h-9 bg-transparent text-white text-center text-sm outline-none border-x border-white/15 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        onClick={() => increment(it.product_id)}
                        data-testid={`cart-item-increment-${it.product_id}`}
                        className="w-8 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        aria-label="Increase"
                      >
                        <Plus size={12} />
                      </button>
                      <span className="px-2 text-[10px] uppercase tracking-[0.22em] text-white/55">{it.unit}</span>
                    </div>
                    <div className="font-display text-lg text-white tabular-nums">
                      {formatAUD(it.lineTotal)}
                    </div>
                  </div>
                  {it.unit === 'kg' && !it.isBulk && it.quantity >= bulkKg - 2 && (
                    <div className="mt-2 text-[10px] text-white/45">
                      Add {(bulkKg - it.quantity).toFixed(1)}kg more for {bulkPct}% bulk discount.
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Fulfilment picker */}
            <div className="px-6 py-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">
                How would you like it?
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {Object.entries(MODE_META).map(([mode, meta]) => {
                  const Icon = meta.icon;
                  const active = fulfilment.mode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => onModeChange(mode)}
                      data-testid={`cart-fulfilment-${mode}`}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 border transition-all ${
                        active
                          ? 'border-[var(--mw-primary)] bg-[var(--mw-primary)]/10 text-white'
                          : 'border-white/15 hover:border-white/30 text-white/70'
                      }`}
                    >
                      <Icon size={16} />
                      <span className="text-[10px] uppercase tracking-[0.2em] font-medium">{meta.label}</span>
                      <span className="text-[9px] text-white/55 leading-none text-center">{meta.hint}</span>
                    </button>
                  );
                })}
              </div>

              {modeCfg && (
                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">
                      Choose {fulfilment.mode === 'pickup' ? 'pickup' : 'delivery'} date
                    </span>
                    <select
                      value={fulfilment.slot_date}
                      onChange={(e) => setFulfilmentField({ slot_date: e.target.value })}
                      data-testid="cart-slot-date"
                      className="input"
                    >
                      <option value="">— Select —</option>
                      {currentDates.map((d) => (
                        <option key={d.date} value={d.date}>{d.label}</option>
                      ))}
                    </select>
                  </label>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">Time window</span>
                    <div className="grid grid-cols-2 gap-2">
                      {currentWindows.map((w) => (
                        <button
                          key={w.code}
                          type="button"
                          onClick={() => setFulfilmentField({ slot_window: w.code })}
                          data-testid={`cart-slot-window-${w.code}`}
                          className={`text-[10px] uppercase tracking-[0.18em] py-2.5 px-3 border transition-colors text-left ${
                            fulfilment.slot_window === w.code
                              ? 'border-[var(--mw-primary)] bg-[var(--mw-primary)]/10 text-white'
                              : 'border-white/15 text-white/60 hover:border-white/30'
                          }`}
                        >
                          {w.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                    {slots?.cutoff_note}
                  </div>
                </div>
              )}
            </div>

            {/* Recurring picker */}
            <div className="px-6 py-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">
                Delivery frequency
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: null, label: 'One-off', hint: 'Just this order' },
                  { key: 'weekly', label: 'Weekly', hint: 'Save 5%' },
                  { key: 'fortnightly', label: 'Fortnightly', hint: 'Save 5%' },
                  { key: 'monthly', label: 'Monthly', hint: 'Save 5%' },
                ].map((r) => {
                  const active = recurring === r.key;
                  return (
                    <button
                      key={r.key ?? 'one'}
                      type="button"
                      onClick={() => setRecurring(r.key)}
                      data-testid={`cart-recurring-${r.key ?? 'one'}`}
                      className={`flex flex-col items-start py-2.5 px-3 border transition-colors ${
                        active
                          ? 'border-[var(--mw-primary)] bg-[var(--mw-primary)]/10 text-white'
                          : 'border-white/15 text-white/70 hover:border-white/30'
                      }`}
                    >
                      <span className="text-[10px] uppercase tracking-[0.22em] font-medium">{r.label}</span>
                      <span className={`text-[9px] mt-1 ${r.key ? 'text-emerald-400' : 'text-white/45'}`}>{r.hint}</span>
                    </button>
                  );
                })}
              </div>
              {recurring && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                  ✓ 5% off every {recurring === 'fortnightly' ? '2 weeks' : recurring === 'monthly' ? 'month' : 'week'} · Cancel anytime
                </div>
              )}
            </div>

            {/* Referral code */}
            <div className="px-6 py-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3 flex items-center gap-2">
                <Gift size={13} /> Referral code
              </div>
              {referralCode && refCheck.valid ? (
                <div
                  data-testid="cart-referral-applied"
                  className="flex items-center justify-between gap-3 border border-emerald-500/40 bg-emerald-500/[0.06] px-3 py-2.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display text-sm text-white truncate">{referralCode}</div>
                      <div className="text-[10px] text-emerald-300 uppercase tracking-[0.22em] mt-0.5">
                        {refCheck.message}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setReferralCode(''); setRefInput(''); }}
                    data-testid="cart-referral-remove"
                    className="text-white/50 hover:text-[var(--mw-primary)] transition-colors"
                    aria-label="Remove code"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : referralCode && refCheck.status === 'checking' ? (
                <div
                  data-testid="cart-referral-checking"
                  className="flex items-center gap-2 border border-white/15 bg-white/[0.02] px-3 py-2.5 text-[10px] uppercase tracking-[0.22em] text-white/60"
                >
                  <Loader2 size={12} className="animate-spin" />
                  Checking {referralCode}…
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={refInput}
                    onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                    placeholder="MW-XXXXXX"
                    data-testid="cart-referral-input"
                    className="input flex-1 uppercase tracking-widest"
                    maxLength={16}
                  />
                  <button
                    type="button"
                    disabled={!refInput}
                    onClick={() => setReferralCode(refInput)}
                    data-testid="cart-referral-apply"
                    className="text-[10px] uppercase tracking-[0.22em] border border-white/20 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] disabled:opacity-40 text-white px-4 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
              {referralCode && !refCheck.valid && refCheck.status === 'done' && (
                <div
                  data-testid="cart-referral-error"
                  className="mt-2 text-[10px] uppercase tracking-[0.22em] text-amber-400"
                >
                  {refCheck.message}
                </div>
              )}
              {recurring && (
                <div className="mt-3 flex items-start gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                  <Gift size={12} className="mt-0.5" />
                  Extra 10% off your first subscription order — auto-applied at checkout for new subscribers
                </div>
              )}
            </div>

            {/* Shipping progress bar */}
            {freeThreshold > 0 && (
              <div className="px-6 py-3 border-t border-white/10">
                {freeRemaining > 0 ? (
                  <div className="text-[11px] text-white/60 leading-relaxed">
                    Add <span className="text-white font-medium">{formatAUD(freeRemaining)}</span> more for
                    <span className="text-[var(--mw-primary)]"> FREE {fulfilment.mode} delivery</span>.
                  </div>
                ) : (
                  <div className="text-[11px] text-emerald-400 uppercase tracking-[0.22em]">
                    Free {fulfilment.mode} delivery unlocked
                  </div>
                )}
                <div className="mt-2 h-1 bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-[var(--mw-primary)] transition-all"
                    style={{ width: `${Math.min(100, (subtotal / freeThreshold) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="px-6 py-4 space-y-1.5 border-t border-white/10 text-sm">
              <div className="flex items-center justify-between text-white/70">
                <span className="uppercase tracking-[0.22em] text-[11px]">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-white tabular-nums">{formatAUD(subtotal)}</span>
              </div>
              {totalSavings > 0 && (
                <div className="flex items-center justify-between text-emerald-400">
                  <span className="uppercase tracking-[0.22em] text-[11px]">Bulk savings</span>
                  <span data-testid="cart-savings" className="font-display tabular-nums">−{formatAUD(totalSavings)}</span>
                </div>
              )}
              {referralCode && refCheck.valid && (
                <div className="flex items-center justify-between text-emerald-400">
                  <span className="uppercase tracking-[0.22em] text-[11px]">Referral $20 off</span>
                  <span data-testid="cart-referral-discount" className="font-display tabular-nums">−{formatAUD(20)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-white/70">
                <span className="uppercase tracking-[0.22em] text-[11px]">
                  {fulfilment.mode === 'pickup' ? 'Pickup' : 'Delivery'}
                </span>
                <span className="font-display text-white tabular-nums">
                  {shipping === 0 ? 'FREE' : formatAUD(shipping)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="uppercase tracking-[0.24em] text-[11px] text-white">Total</span>
                <span data-testid="cart-total" className="font-display text-2xl text-[var(--mw-primary)] tabular-nums">
                  {formatAUD(Math.max(0, total - (referralCode && refCheck.valid ? 20 : 0)))}
                </span>
              </div>
            </div>

            <div className="px-6 pb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={clear}
                data-testid="cart-clear-all"
                className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[var(--mw-primary)] transition-colors"
              >
                Clear cart
              </button>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                AUD · GST inc.
              </div>
            </div>

            {!meetsMin && (
              <div className="mx-6 mt-3 flex items-start gap-2 border border-amber-500/40 bg-amber-500/5 text-amber-300 text-[11px] uppercase tracking-[0.18em] px-3 py-2">
                <Info size={14} className="shrink-0 mt-0.5" />
                Add <span className="text-white font-medium normal-case tracking-normal">{formatAUD(minOrderShort)}</span> more —
                <span className="normal-case tracking-normal">minimum {formatAUD(config?.min_order_total || 50)}.</span>
              </div>
            )}

            <form onSubmit={onCheckout} className="px-6 py-6 border-t border-white/10 space-y-3 mt-3">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">Your details</div>
              <CField label="Full name" testId="cart-form-name" value={form.name} onChange={set('name')} required />
              <div className="grid grid-cols-2 gap-3">
                <CField label="Email" type="email" testId="cart-form-email" value={form.email} onChange={set('email')} required />
                <CField label="Phone" testId="cart-form-phone" value={form.phone} onChange={set('phone')} required />
              </div>

              {error && (
                <div className="border border-[var(--mw-primary)]/40 text-[var(--mw-primary)] text-xs uppercase tracking-[0.22em] px-4 py-3">
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || items.length === 0 || !meetsMin || !slotChosen}
                data-testid="cart-checkout-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 text-xs uppercase tracking-[0.22em] transition-colors"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
                {submitting ? 'Redirecting…' : `Checkout · ${formatAUD(Math.max(0, total - (referralCode && refCheck.valid ? 20 : 0)))}`}
              </button>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 text-center">
                Secured by Stripe · WA only
              </div>
            </form>
          </div>
        )}
      </aside>
    </>
  );
}

function CField({ label, testId, type = 'text', value, onChange, required }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">{label}</span>
      <input type={type} value={value} onChange={onChange} data-testid={testId} required={required} className="input" />
    </label>
  );
}
